import React, { useCallback, useMemo, useState } from 'react';
import { useFocusAnchor } from '../lib/focusAnchor';
import {
  useRecordContext,
  ReferenceField,
  FunctionField,
  SimpleForm,
  useGetManyReference,
  useGetMany,
  useGetList,
  useGetOne,
  useTranslate,
  useNotify,
  useRedirect,
  useUpdate,
  useRefresh,
  DateTimeInput,
} from 'react-admin';
import { EditInDialogButton, EditDialog } from '@react-admin/ra-form-layout';
import {
  ClipboardList,
  Truck,
  User,
  Users,
  Handshake,
  Globe,
  FileText,
  Plus,
  Eye,
  Pencil,
} from 'lucide-react';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Switch,
  TextField,
  Stack,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Funeral, DeathBenefitsStatus } from '../types';
import { PaperworkCompleteNotificationDialog } from '../components/PaperworkCompleteNotificationDialog';
import { cloneDefaultLines, urnLinesFor } from 'paperman-contract-defaults';
import { DocumentsTab } from './documents/DocumentsTab';
import { ClientSaveToolbar } from '../clients/ClientSaveToolbar';
import ClientEditBase from '../clients/ClientEditBase';
import { ContactCard } from '../components/ContactCard';
import { ProcessTimeline } from './components/ProcessTimeline';
import ValidationStatusIndicator from './ValidationStatusIndicator';
import OasCancellationModal from './OasCancellationModal';
import { PlaceOfDeathValue, hasPlaceOfDeath } from './components/PlaceOfDeathModal';
import { FuneralTransportZone } from './FuneralTransportZone';
import BenefitFormReviewModal, { BenefitFormReviewConfig } from './BenefitFormReviewModal';
import { SidebarStatusBadge, ClickableFormRow } from './SidebarStatusBadge';
import { OntarioPaperworkRows } from './OntarioPaperworkRows';
import CppBenefitsSection from './CppBenefitsSection';
import { RrqEnvoiRow } from './RrqEnvoiModal';
import { canSendToRrq } from './rrqEnvoi';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { resolveBenefitsProgram } from './benefitsStatus';
import {
  BenefitsHeadlineBadge,
  BenefitsStatusOverrideMenu,
  BenefitsStatusNote,
  BenefitsStatusRefreshProvider,
} from './BenefitsStatusField';
import {
  PartnerVisibilityToggle,
  DeliveryCard,
  UrnCard,
  deliveryStatus,
  CremationPartnersDisplay,
} from './components/FuneralShowHelpers';
import { OnboardingHistorySection } from './components/OnboardingHistory';
import { SidebarCoronerModal, useFuneralCoroner } from './SidebarCoronerModal';
import { NotesCard } from './components/NotesCard';
import { RemainsLine } from './components/RemainsLine';
import { DropoffSidebarSection } from './DropoffSidebarSection';
import { PreplanSidebarSection, PreplanContractsNote } from './PreplanSidebarSection';
import { useSidebarTabRequests } from './sidebarTabs';
import { TrustageClaimSection } from './TrustageClaimSection';
import { SiedStatusSidebarRow } from './SiedStatusPill';
import { caseContacts, CaseContactRole } from './caseContacts';
import {
  countDeathCertificates,
  describeBreakdown,
  ContractForCount,
  PaymentForCount,
} from './deathCertificateCounts';

// ─── Reusable label-value row ────────────────────────────────────────────────

// Click-to-toggle variant for boolean fields on the funeral record (DEC,
// Onboarding 1/2). Same visuals as SidebarStatusBadge plus a confirmation
// dialog so the agent doesn't flip the flag accidentally.
function SidebarStatusToggle({
  source,
  readyLabel = 'Ready',
  notReadyLabel = 'Pending',
}: {
  source: string;
  readyLabel?: string;
  notReadyLabel?: string;
}) {
  const record = useRecordContext();
  const [update] = useUpdate();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  if (!record) return null;
  const checked = !!(record as any)[source];

  const handleToggle = async () => {
    try {
      await update(
        'funerals',
        {
          id: (record as any).id,
          data: { ...(record as any), [source]: !checked },
          previousData: record as any,
        },
        {
          onSuccess: () => {
            notify('Status updated', { type: 'success' });
            setOpen(false);
          },
          onError: () => notify('Error updating status', { type: 'error' }),
        },
      );
    } catch {
      notify('Error updating status', { type: 'error' });
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center gap-1.5 cursor-pointer rounded hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <SidebarStatusBadge confirmed={checked} readyLabel={readyLabel} notReadyLabel={notReadyLabel} />
      </button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>{checked ? `Mark as ${notReadyLabel.toLowerCase()}?` : `Mark as ${readyLabel.toLowerCase()}?`}</DialogTitle>
        <DialogActions>
          <Button onClick={(e) => { e.stopPropagation(); setOpen(false); }}>Cancel</Button>
          <Button variant="contained" onClick={(e) => { e.stopPropagation(); void handleToggle(); }}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// DEC-specific sidebar toggle: same compact visual as SidebarStatusToggle but
// the dialog also captures the death certificate confirmation number and
// triggers the "paperwork complete" customer-notification flow once both DEC
// and Death Benefits are marked complete (Quebec only).
function SidebarDecToggle() {
  useFocusAnchor('dec');
  const record = useRecordContext<Funeral>();
  const [update] = useUpdate();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [decReady, setDecReady] = useState(false);
  const [deathCertificateNumber, setDeathCertificateNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [paperworkDoneOpen, setPaperworkDoneOpen] = useState(false);
  const [funeralForNotification, setFuneralForNotification] = useState<Funeral | null>(null);

  if (!record) return null;
  const checked = !!record.dec_ready;

  const handleOpen = () => {
    setDecReady(checked);
    setDeathCertificateNumber(record.death_certificate_number || '');
    setOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const wasComplete = checked;
    const isQuebec = ((record.region as string) || '').toLowerCase() === 'quebec';

    try {
      await update(
        'funerals',
        {
          id: record.id,
          data: {
            ...record,
            dec_ready: decReady,
            death_certificate_number: deathCertificateNumber || null,
          },
          previousData: record,
        },
        {
          mutationMode: 'pessimistic',
          onSuccess: (data: any) => {
            notify('Status updated', { type: 'success' });
            setOpen(false);
            const merged = (data as Funeral) || ({
              ...(record as Funeral),
              dec_ready: decReady,
              death_certificate_number: deathCertificateNumber || null,
            } as Funeral);
            const decTransitioned = !wasComplete && !!merged.dec_ready;
            const benefitsNowComplete =
              merged.death_benefits_status === DeathBenefitsStatus.COMPLETED;
            if (isQuebec && decTransitioned && benefitsNowComplete) {
              setFuneralForNotification(merged);
              setPaperworkDoneOpen(true);
            }
          },
          onError: () => notify('Error updating status', { type: 'error' }),
        },
      );
    } catch {
      notify('Error updating status', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <span data-notes-anchor="dec">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); handleOpen(); }}
        className="inline-flex items-center gap-1.5 cursor-pointer rounded hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <SidebarStatusBadge confirmed={checked} readyLabel="Completed" notReadyLabel="Pending" />
      </button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        onClick={(e) => e.stopPropagation()}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>DEC Complete</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ minWidth: 280, my: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography>DEC complete</Typography>
              <Switch
                checked={decReady}
                onChange={(e) => setDecReady(e.target.checked)}
                color="primary"
              />
            </Box>
            <TextField
              fullWidth
              label="Death Certificate Number"
              value={deathCertificateNumber}
              onChange={(e) => setDeathCertificateNumber(e.target.value)}
              placeholder="Enter confirmation number"
              variant="outlined"
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={(e) => { e.stopPropagation(); setOpen(false); }} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={(e) => { e.stopPropagation(); void handleSave(); }}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <PaperworkCompleteNotificationDialog
        open={paperworkDoneOpen}
        onClose={() => setPaperworkDoneOpen(false)}
        funeral={funeralForNotification}
      />
    </span>
  );
}

// ─── Death certificates ordered ─────────────────────────────────────────────

// How many certificates the family actually paid for, so the agent filing the
// DEC knows how many to order and at which processing speed. Paid add-on
// contracts add to the order; unpaid ones are left out and only mentioned in
// the tooltip (T-121). See deathCertificateCounts.ts for the rules.
const AMBER = 'text-amber-600 dark:text-amber-500';

function DeathCertificateOrderRows() {
  const record = useRecordContext<Funeral>();
  const { data: contracts } = useGetManyReference(
    'contracts',
    {
      target: 'funeral_id',
      id: record?.id ?? 0,
      pagination: { page: 1, perPage: 50 },
      sort: { field: 'created_at', order: 'ASC' },
    },
    { enabled: !!record?.id },
  );

  // sb_payments is keyed by contract_id, not funeral_id. The data provider
  // turns a plain array filter into `eq.1,2` (which matches nothing once a case
  // has two contracts), so spell out PostgREST's `in` operator.
  const contractIds = (contracts ?? []).map((c) => c.id);
  const { data: payments, isLoading: paymentsLoading } = useGetList(
    'sb_payments',
    {
      filter: { 'contract_id@in': `(${contractIds.join(',')})` },
      pagination: { page: 1, perPage: 200 },
      sort: { field: 'created_at', order: 'ASC' },
    },
    { enabled: contractIds.length > 0 },
  );

  const counts = useMemo(
    () =>
      countDeathCertificates(
        (contracts ?? []) as ContractForCount[],
        (payments ?? []) as PaymentForCount[],
      ),
    [contracts, payments],
  );

  // Don't flash an unpaid count while payments are still loading.
  if (contractIds.length > 0 && paymentsLoading) return null;
  if (counts.perContract.length === 0) return null;

  const breakdown = describeBreakdown(counts);
  const hint = counts.unpaidOnly
    ? 'not paid'
    : counts.excludedUnpaid > 0
      ? `+${counts.excludedUnpaid} unpaid`
      : counts.excludedUnpaidUpgrades > 0
        ? 'unpaid upgrade'
        : null;
  const countClass = counts.unpaidOnly ? AMBER : 'text-foreground';

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="pl-1 pb-1 space-y-0.5 cursor-default" data-testid="dc-order-rows">
            <div className="flex justify-between items-center">
              <span className="text-[12px] text-muted-foreground">Normal processing</span>
              <span className="text-[12px] tabular-nums flex items-center gap-1.5">
                {hint && <span className={`text-[11px] ${AMBER}`}>{hint}</span>}
                <span className={countClass}>×{counts.standard}</span>
              </span>
            </div>
            {counts.expedited > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-[12px] text-muted-foreground">Accelerated processing</span>
                <span className={`text-[12px] tabular-nums ${countClass}`}>×{counts.expedited}</span>
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-xs text-xs">
          {counts.unpaidOnly
            ? `No payment on file for any contract — confirm payment before ordering. ${breakdown}`
            : breakdown}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// A status row whose name (blue link) and badge both open a review/edit modal.
// Used for the benefit forms so every status is clickable, consistent with the
// DEC/Benefits verify links.
// "Cancel OAS" (ISP-1201) lifecycle status. Unlike the other benefit rows
// (which only show confirmed/pending), this flow is automated end-to-end
// (auto-email → sign → auto-fax), so the team needs to see exactly where it is.
const OAS_STATUS_DISPLAY: Record<string, { label: string; cls: string }> = {
  not_applicable: { label: 'N/A', cls: 'bg-gray-100 text-gray-500' },
  not_ready: { label: 'Not ready', cls: 'bg-gray-100 text-gray-600' },
  missing_information: { label: 'Missing info', cls: 'bg-amber-100 text-amber-800' },
  ready_to_send: { label: 'Ready to send', cls: 'bg-violet-100 text-violet-800' },
  sent_for_signature: { label: 'Sent for signature', cls: 'bg-blue-100 text-blue-800' },
  signature_received: { label: 'Signature received', cls: 'bg-indigo-100 text-indigo-800' },
  fax_sent: { label: 'Fax sent', cls: 'bg-green-100 text-green-800' },
};

function OasCancellationRow({ record, onClick }: { record: any; onClick: () => void }) {
  const status = (record?.oas_cancellation_status as string) || 'not_ready';
  const faxFailed =
    record?.oas_cancellation_fax_status === 'failed' && status !== 'fax_sent';
  const display = faxFailed
    ? { label: 'Fax failed', cls: 'bg-red-100 text-red-800' }
    : OAS_STATUS_DISPLAY[status] || OAS_STATUS_DISPLAY.not_ready;
  const missing: string[] = Array.isArray(record?.oas_cancellation_missing_fields)
    ? record.oas_cancellation_missing_fields
    : [];
  // Native title tooltip: what's missing, or the fax confirmation.
  let title: string | undefined;
  if (status === 'missing_information' && missing.length) {
    title = `Missing: ${missing.join(', ')}`;
  } else if (status === 'ready_to_send') {
    title = 'Ready — open to review and send to the family';
  } else if (status === 'fax_sent' && record?.oas_cancellation_faxed_at) {
    title = `Faxed ${new Date(record.oas_cancellation_faxed_at).toLocaleString()}${
      record?.oas_cancellation_fax_id ? ` · ${record.oas_cancellation_fax_id}` : ''
    }`;
  } else if (faxFailed) {
    title = 'Fax to Service Canada failed — open to re-send';
  }
  return (
    <div className="flex justify-between items-center py-1.5">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        style={{ color: '#2563eb' }}
        className="text-[13px] hover:underline leading-5 shrink-0 focus:outline-none focus:ring-2 focus:ring-ring rounded text-left font-medium"
      >
        Cancel OAS
      </button>
      <button
        type="button"
        title={title}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        className="inline-flex items-center cursor-pointer rounded hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${display.cls}`}>
          {display.label}
        </span>
      </button>
    </div>
  );
}

// Review-modal config per benefit form key. CPP forms additionally persist the
// flattened PDF (saveFlattened) for DocuSeal; QPP forms are download-only.
const REVIEW_CONFIGS: Record<string, BenefitFormReviewConfig> = {
  cpp_death_benefit: {
    title: 'CPP Death Benefit', pdfPath: 'cpp-benefits/death-benefit-form',
    formKey: 'cpp_death_benefit', saveFlattened: true, downloadName: 'CPP_DeathBenefit.pdf',
  },
  cpp_survivor_pension: {
    title: "CPP Survivor's Pension", pdfPath: 'cpp-benefits/form',
    formKey: 'cpp_survivor_pension', saveFlattened: true, downloadName: 'CPP_SurvivorBenefits.pdf',
  },
  qpp_benefits: {
    title: 'QPP Benefits', pdfPath: 'survivor-benefits/form',
    formKey: 'qpp_benefits', saveFlattened: false, downloadName: 'QPP_Benefits.pdf',
  },
  qpp_proof_of_payment: {
    title: 'Proof of Payment', pdfPath: 'proof-of-payment',
    formKey: 'qpp_proof_of_payment', saveFlattened: false, downloadName: 'Proof_of_Payment.pdf',
  },
};

// Program-aware rows that mirror the CPP / QPP confirmation state stored at
// funerals.form_confirmations.<key>. CPP cases see CPP rows; QPP cases see
// QPP rows — resolved via resolveBenefitsProgram (per-case benefits_program
// override, else region). CPP Survivor's Pension only appears when there is
// a current spouse / under-18 child / 18-25 student — same gate as the
// SurvivorBenefitsButton modal tab. Each row's name + badge open the form's
// review modal (where "Ready" flips its status).
function FormConfirmationStatusRows() {
  const record = useRecordContext<Funeral>();
  // Need the deceased's marital status name to gate the survivor-pension row.
  const { data: deceasedClient } = useGetOne(
    'clients',
    { id: (record as any)?.deceased_client_id },
    { enabled: !!(record as any)?.deceased_client_id },
  );
  const { data: maritalStatus } = useGetOne(
    'marital_statuses',
    { id: (deceasedClient as any)?.marital_status_id },
    { enabled: !!(deceasedClient as any)?.marital_status_id },
  );
  const [oasOpen, setOasOpen] = useState(false);
  // Board "OAS form not signed" item → open the OAS panel (T-186).
  useFocusAnchor('oas', () => setOasOpen(true));
  const [reviewConfig, setReviewConfig] = useState<BenefitFormReviewConfig | null>(null);
  // T-154: send the QPP packet to Retraite Québec through Envoi numérique.
  const rrqSendEnabled = useFeatureFlag('rrq_digital_send_enabled');
  const refreshRecord = useRefresh();

  if (!record) return null;

  const region = ((record as any).region || '').toLowerCase();
  const isOntario = region === 'ontario';
  const program = resolveBenefitsProgram(record);
  const formConfirmations = (record as any).form_confirmations || {};
  const isConfirmed = (key: string) => !!formConfirmations[key]?.confirmed_at;
  // A packet marked "Completed" (QC files QPP directly) means both QPP forms are
  // done — so those per-form rows shouldn't still read "Pending" just because the
  // agent downloaded without clicking Save. Scoped to the QPP rows only.
  const benefitsCompleted =
    ((record as any).death_benefits_status || '').toString().toLowerCase() === 'completed';

  // Benefit rows (CPP/QPP death benefit + survivor / proof of payment) are
  // grouped under the Benefits headline and bracketed by dividers, keyed on the
  // resolved program. Coroner is a separate, non-benefit row that sits after
  // the divider with OAS and stays keyed on region (an Ontario-file concern
  // regardless of which program the case applies for).
  let programRows: React.ReactNode = null;
  const coronerRow: React.ReactNode = isOntario ? (
    <Row label="Coroner">
      <CoronerCell funeralId={(record as any).id} />
    </Row>
  ) : null;
  if (program === 'cpp') {
    const maritalLabel = ((maritalStatus as any)?.name || '').toLowerCase();
    // Match the ENGLISH half of each bilingual marital_statuses.name — every
    // label carries both, and the English side is collision-free.
    //
    // Do NOT match 'conjoint': "Ex-conjoint/Ex-Spouse" contains it, so a
    // deceased whose de facto relationship had ENDED was read as having a
    // current partner and the survivor-pension row was shown for a case that
    // has no surviving spouse.
    //
    // Keep in sync with isSurvivorApplicable in
    // packages/nestjs/src/benefits-status/benefits-status.service.ts.
    const hasCurrentSpouse = ['married', 'civil union', 'common-law', 'legally separated']
      .some((s) => maritalLabel.includes(s));
    const sb = (record as any).survivor_benefits_data;
    const has1825Student = Array.isArray(sb?.children18to25?.children)
      && sb.children18to25.children.some((c: any) => c?.attendingSchool === 'yes');
    const showSurvivorPensionRow = hasCurrentSpouse
      || (record as any).children_under_18_at_death === true
      || has1825Student;
    programRows = (
      /* CPP rows + their review/send/discard flow — single entry point now
         that the TopToolbar "CPP Benefits" button is gone. */
      <CppBenefitsSection
        record={record}
        deceasedClient={deceasedClient}
        showSurvivorPension={showSurvivorPensionRow}
      />
    );
  } else {
    programRows = (
      <>
        <ClickableFormRow
          label="QPP Form"
          confirmed={isConfirmed('qpp_benefits') || benefitsCompleted}
          onClick={() => setReviewConfig(REVIEW_CONFIGS.qpp_benefits)}
        />
        <ClickableFormRow
          label="Proof of Payment Form"
          confirmed={isConfirmed('qpp_proof_of_payment') || benefitsCompleted}
          onClick={() => setReviewConfig(REVIEW_CONFIGS.qpp_proof_of_payment)}
        />
        {/* Quebec cases only (region, not benefits_program). */}
        {canSendToRrq(record, rrqSendEnabled) && (
          <RrqEnvoiRow funeralId={Number((record as any).id)} onSent={refreshRecord} />
        )}
      </>
    );
  }

  // "Cancel OAS" (ISP-1201) — shown for every funeral, below the program rows.
  // Clicking opens the preview/send modal (it also sends for signature/faxes).
  const deceasedName = [
    (deceasedClient as any)?.first_name,
    (deceasedClient as any)?.last_name,
  ].filter(Boolean).join(' ');

  return (
    <>
      {programRows}
      {/* Close the Benefits group; Coroner + OAS are separate rows below it. */}
      {programRows && <div className="border-t border-border my-1" />}
      {coronerRow}
      <div data-notes-anchor="oas">
        <OasCancellationRow record={record} onClick={() => setOasOpen(true)} />
      </div>
      {oasOpen && (
        <OasCancellationModal
          open={oasOpen}
          onClose={() => setOasOpen(false)}
          funeralId={(record as any).id}
          funeralUuid={(record as any).uuid}
          confirmed={isConfirmed('oas_cancellation')}
          deceasedName={deceasedName}
          defaultLanguage={
            formConfirmations.oas_cancellation?.language ||
            ((record as any).language === 'fr' ? 'fr' : 'en')
          }
        />
      )}
      <BenefitFormReviewModal
        open={!!reviewConfig}
        onClose={() => setReviewConfig(null)}
        funeralId={(record as any).id}
        config={reviewConfig}
        confirmed={!!reviewConfig && isConfirmed(reviewConfig.formKey)}
      />
    </>
  );
}

// Coroner sidebar control. Ontario funerals only — the calendar source is the
// OCC Toronto West public Google Calendar. Read state from funeral_coroners
// (1:1 with funerals via UNIQUE constraint).
function CoronerCell({ funeralId }: { funeralId: number }) {
  const [open, setOpen] = useState(false);
  const { row } = useFuneralCoroner(funeralId);

  // Always render the control (Select if unset, name link if set). No loading
  // skeleton — if the lookup is in flight we still want the Select button to
  // be available, and if it errors (e.g. table not migrated) we fall through
  // to Select rather than showing nothing.
  return (
    <>
      {row ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className="text-[13px] text-foreground hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded"
        >
          {row.coroner_name_snapshot}
        </button>
      ) : (
        <Button
          size="small"
          variant="outlined"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          sx={{ py: 0, px: 1, minHeight: 0, fontSize: 12 }}
        >
          Select
        </Button>
      )}
      {open && (
        <SidebarCoronerModal
          open={open}
          onClose={() => setOpen(false)}
          funeralId={funeralId}
          existing={row}
        />
      )}
    </>
  );
}

function SidebarDateTime({ value }: { value?: string | null }) {
  if (!value) return <span className="text-muted-foreground text-xs">Not set</span>;
  return (
    <span>
      {new Date(value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })}
    </span>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-1.5">
      <span className="text-[13px] text-muted-foreground leading-5 shrink-0">{label}</span>
      <div className="text-[13px] text-foreground leading-5 min-w-0">{children}</div>
    </div>
  );
}

// ─── Client section — one per role ───────────────────────────────────────────

// The client name itself is the edit trigger — clicking it opens the edit
// dialog. We render a plain <button> (not react-admin's EditInDialogButton,
// which collapses to an icon-only control on small screens, hiding the name on
// mobile) and drive a controlled EditDialog by `id`. Module-level so its type
// identity is stable across renders (an in-component definition would remount
// and close an open edit dialog).
function ClientNameEditor({ name, clientId }: { name: string; clientId?: number }) {
  const [open, setOpen] = useState(false);

  // No client record to edit (e.g. a free-text legacy name) — show plain text.
  if (clientId == null) {
    return <span className="font-medium">{name || '—'}</span>;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="font-medium text-left cursor-pointer bg-transparent border-0 p-0 hover:underline focus:underline focus:outline-none"
      >
        {name || '—'}
      </button>
      <EditDialog
        fullWidth
        maxWidth="md"
        isOpen={open}
        open={() => setOpen(true)}
        close={() => setOpen(false)}
        resource="clients"
        id={clientId}
      >
        <SimpleForm toolbar={<ClientSaveToolbar />}>
          <ClientEditBase />
        </SimpleForm>
      </EditDialog>
    </>
  );
}

function ClientSection({
  source,
  role,
  extraRows,
  hideAddress,
  showSpouse,
}: {
  source: string;
  role: string;
  extraRows?: React.ReactNode;
  hideAddress?: boolean;
  showSpouse?: boolean;
}) {
  const record = useRecordContext<Funeral>();
  if (!record) return null;

  const clientId = (record as any)[source];
  if (!clientId) return null;

  // Relationship to the deceased is stored per-role on the funeral
  // (e.g. primary_client_relationship). Shown on the card for non-deceased roles.
  const relationship =
    source !== 'deceased_client_id'
      ? (record as any)[source.replace(/_id$/, '_relationship')]
      : undefined;

  const isDeceased = source === 'deceased_client_id';
  const funeralId = record?.id != null ? Number(record.id) : undefined;

  return (
    <AccordionItem value={source}>
      <AccordionTrigger icon={<User className="w-4 h-4" />}>
        {role}
      </AccordionTrigger>
      <AccordionContent>
        <ReferenceField source={source} reference="clients" link={false}>
          <FunctionField
            render={(client: any) => {
              const card = (
                <ContactCard
                  contact={{
                    name: client.name,
                    phone: client.phone_number,
                    email: client.email,
                    clientId: client.id != null ? Number(client.id) : undefined,
                  }}
                  context={{ funeralId }}
                  // The name is the edit trigger for every client (incl. the
                  // deceased). The deceased card has no contact actions; others
                  // also get mail/SMS/email in the footer.
                  renderName={() => (
                    <ClientNameEditor
                      name={client.name}
                      clientId={client.id != null ? Number(client.id) : undefined}
                    />
                  )}
                  {...(isDeceased ? { hideActions: true } : { enableMail: true })}
                >
                  {relationship && (
                    <Row label="Relationship">
                      <span>{relationship}</span>
                    </Row>
                  )}

                  {!hideAddress && client.address && (
                    <Row label="Address">
                      <span>{client.address}</span>
                    </Row>
                  )}

                  {extraRows}
                </ContactCard>
              );

              if (!isDeceased) return card;

              // Deceased section: the deceased's own (action-less) card, then the
              // spouse as a separate card with its own contact actions.
              return (
                <div className="space-y-3">
                  {card}

                  {showSpouse && client.spouse_id && (
                    <ReferenceField source="spouse_id" reference="clients" link={false}>
                      <FunctionField
                        render={(spouse: any) => (
                          <ContactCard
                            contact={{
                              name: spouse.name,
                              phone: spouse.phone_number,
                              email: spouse.email,
                              clientId: spouse.id != null ? Number(spouse.id) : undefined,
                            }}
                            context={{ funeralId }}
                            enableMail
                            renderName={() => (
                              <ClientNameEditor
                                name={spouse.name}
                                clientId={spouse.id != null ? Number(spouse.id) : undefined}
                              />
                            )}
                          >
                            <Row label="Relationship">
                              <span>Spouse</span>
                            </Row>
                          </ContactCard>
                        )}
                      />
                    </ReferenceField>
                  )}

                  {showSpouse && client.legacy_spouse && !client.spouse_id && (
                    <ContactCard
                      contact={{ name: client.legacy_spouse }}
                      context={{ funeralId }}
                      hideActions
                    >
                      <Row label="Relationship">
                        <span>Spouse</span>
                      </Row>
                    </ContactCard>
                  )}
                </div>
              );
            }}
          />
        </ReferenceField>
      </AccordionContent>
    </AccordionItem>
  );
}

// ─── Case contacts — everyone on the case, with what they are on it ──────────

const ROLE_BADGE_CLASS: Record<CaseContactRole, string> = {
  'Main contact': 'bg-blue-100 text-blue-700',
  Payer: 'bg-emerald-100 text-emerald-700',
  Authorized: 'bg-amber-100 text-amber-800',
  Declarant: 'bg-slate-100 text-slate-700',
  Enquirer: 'bg-purple-100 text-purple-700',
};

function FuneralClientsSidebarSection() {
  const record = useRecordContext<Funeral>();

  const { data: funeralClients } = useGetManyReference(
    'funeral_clients',
    { target: 'funeral_id', id: record?.id ?? 0 },
    { enabled: !!record?.id },
  );

  // The enquirer is whoever the case's lead belongs to (leads.client_id),
  // reached through the quote that carries this funeral.
  const { data: quotes } = useGetList(
    'quotes',
    { filter: { funeral_id: record?.id }, pagination: { page: 1, perPage: 10 } },
    { enabled: !!record?.id },
  );
  const leadIds = useMemo(
    () => Array.from(new Set((quotes ?? []).map((q: any) => q.lead_id).filter(Boolean))),
    [quotes],
  );
  const { data: leads } = useGetMany('leads', { ids: leadIds }, { enabled: leadIds.length > 0 });

  const contacts = useMemo(
    () =>
      caseContacts({
        primaryClientId: record?.primary_client_id as any,
        payingClientId: record?.paying_client_id as any,
        authorizedClientId: record?.authorized_client_id as any,
        declarantClientId: record?.declarant_client_id as any,
        deceasedClientId: record?.deceased_client_id as any,
        enquirerClientIds: (leads ?? []).map((l: any) => l.client_id),
        linked: (funeralClients ?? []) as any,
        primaryRelationship: (record as any)?.primary_client_relationship,
        payingRelationship: (record as any)?.paying_client_relationship,
        authorizedRelationship: (record as any)?.authorized_client_relationship,
      }),
    [record, leads, funeralClients],
  );

  const ids = contacts.map((c) => c.clientId);
  const { data: people } = useGetMany('clients', { ids }, { enabled: ids.length > 0 });
  const personById = useMemo(
    () => new Map((people ?? []).map((p: any) => [Number(p.id), p])),
    [people],
  );

  if (!record || contacts.length === 0) return null;
  const funeralId = record.id != null ? Number(record.id) : undefined;

  return (
    <AccordionItem value="case-contacts">
      <AccordionTrigger icon={<Users className="w-4 h-4" />}>
        Case contacts
      </AccordionTrigger>
      <AccordionContent>
        <div className="space-y-3">
          {contacts.map((contact) => {
            const person = personById.get(contact.clientId);
            const name = person?.name || '—';
            return (
              <ContactCard
                key={contact.clientId}
                contact={{
                  name,
                  phone: person?.phone_number,
                  email: person?.email,
                  clientId: contact.clientId,
                }}
                context={{ funeralId }}
                enableMail
                renderName={() => <ClientNameEditor name={name} clientId={contact.clientId} />}
              >
                {contact.roles.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-0.5" data-testid="case-contact-roles">
                    {contact.roles.map((role) => (
                      <span
                        key={role}
                        className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${ROLE_BADGE_CLASS[role]}`}
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                )}
                {contact.relationship && (
                  <Row label="Relationship">
                    <span>{contact.relationship}</span>
                  </Row>
                )}
              </ContactCard>
            );
          })}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

// ─── Quote section ──────────────────────────────────────────────────────────

function QuoteSection({ funeralId }: { funeralId: number | string }) {
  const { data: quotes } = useGetList(
    'quotes',
    { filter: { funeral_id: funeralId }, pagination: { page: 1, perPage: 1 }, sort: { field: 'created_at', order: 'DESC' } },
    { enabled: !!funeralId },
  );
  const quote = quotes?.[0];
  if (!quote) return null;

  return (
    <AccordionItem value="quote">
      <AccordionTrigger icon={<FileText className="w-4 h-4" />}>
        Quote
      </AccordionTrigger>
      <AccordionContent>
        <Row label="Type">
          <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${
            quote.deal_type === 'pre_need'
              ? 'bg-purple-100 text-purple-700'
              : 'bg-blue-100 text-blue-700'
          }`}>
            {quote.deal_type === 'pre_need' ? 'Pre-Need' : 'At-Need'}
          </span>
        </Row>
        {quote.timeline && (
          <Row label="Timeline">
            <span>{quote.timeline}</span>
          </Row>
        )}
        <Row label="Created">
          <span>
            {new Date(quote.created_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
        </Row>
        <a
          href={`/quotes/${quote.id}/show`}
          className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <Eye className="w-3 h-3" />
          Open quote
        </a>
      </AccordionContent>
    </AccordionItem>
  );
}

// ─── Contracts tab ──────────────────────────────────────────────────────────

function ContractsTab() {
  const record = useRecordContext<Funeral>();
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();

  const { data: contracts, isLoading } = useGetManyReference(
    'contracts',
    { target: 'funeral_id', id: record?.id ?? 0 },
    { enabled: !!record?.id },
  );

  // sb_payments is keyed by contract_id, not funeral_id — fetch payments for the funeral's contracts.
  const contractIds = (contracts || []).map((c) => c.id);
  const { data: payments } = useGetList(
    'sb_payments',
    {
      filter: { 'contract_id@in': `(${contractIds.join(',')})` },
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'created_at', order: 'DESC' },
    },
    { enabled: contractIds.length > 0 },
  );

  const handleCreateContract = () => {
    if (!record) return;
    if (!record.primary_client_id) {
      notify(translate('resources.contracts.primary_client_required'), { type: 'error' });
      return;
    }
    redirect('/contracts/create', undefined, undefined, undefined, {
      record: {
        funeral_id: record.id,
        // The contract names the payer; the main contact when nobody else pays.
        client_id: record.paying_client_id ?? record.primary_client_id,
        lines: [
          ...cloneDefaultLines('at', record.region),
          ...urnLinesFor(record.urn_choice, record.region),
        ],
        is_preneed: false,
        is_monthly: false,
        monthly_payment_amount: null,
      },
    });
  };

  if (!record) return null;

  return (
    <div className="px-4 py-3 space-y-3">
      {/* Contracts list */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[13px] font-medium text-foreground">Contracts</span>
          <button
            onClick={handleCreateContract}
            className="flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <Plus className="w-3 h-3" />
            Add
          </button>
        </div>
        {isLoading ? (
          <div className="text-[13px] text-muted-foreground">Loading...</div>
        ) : !contracts || contracts.length === 0 ? (
          <div className="text-[13px] text-muted-foreground">No contracts</div>
        ) : (
          <div className="space-y-1.5">
            {contracts.map((contract: any) => (
              <button
                key={contract.id}
                onClick={() => redirect(`/contracts/${contract.id}/show`)}
                className="w-full text-left rounded-md border px-3 py-2 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-medium text-foreground">
                    #{contract.id}
                  </span>
                  <span className="text-[13px] font-medium text-foreground">
                    ${((contract.total_amount || 0) / 100).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {contract.issue_date && (
                    <span className="text-[11px] text-muted-foreground">
                      {new Date(contract.issue_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                  {contract.is_preneed && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                      Pre-Need
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Payments list */}
      {payments && payments.length > 0 && (
        <div>
          <div className="text-[13px] font-medium text-foreground mb-2">
            Payments
          </div>
          <div className="space-y-1.5">
            {payments.map((payment: any) => {
              const isRefund = payment.transaction_type === 'refund';
              const amountDollars = ((payment.amount_total || 0) / 100).toFixed(2);
              const sourceLabel = isRefund
                ? 'Refund'
                : payment.payment_source === 'stripe'
                  ? 'Stripe'
                  : payment.payment_source === 'partner'
                    ? 'Partner'
                    : 'Payment';
              const status = payment.payment_status || 'pending';
              return (
                <div
                  key={payment.id}
                  className="flex items-center justify-between rounded-md border px-3 py-2"
                >
                  <div>
                    <div className="text-[13px] font-medium">
                      {isRefund ? `-$${amountDollars.replace('-', '')}` : `$${amountDollars}`}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {sourceLabel}
                      {payment.created_at && (
                        <> &middot; {new Date(payment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</>
                      )}
                    </div>
                  </div>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                    status === 'succeeded' || status === 'paid' || status === 'completed'
                      ? 'bg-green-100 text-green-700'
                      : status === 'failed'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-600'
                  }`}>
                    {status}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sidebar ─────────────────────────────────────────────────────────────────

export function FuneralShowSidebar() {
  const record = useRecordContext<Funeral>();
  const [activeTab, setActiveTab] = useState('details');
  // Which sections are open. Not remembered across visits, same as before.
  const [openSections, setOpenSections] = useState<string[]>([
    'preneed',
    'notes',
    'visitation',
    'status',
    'delivery',
    'deceased_client_id',
    'case-contacts',
  ]);
  // "+ Add" on a collapsed Notes section opens it so the composer shows.
  const openNotes = useCallback(
    () => setOpenSections((open) => (open.includes('notes') ? open : [...open, 'notes'])),
    [],
  );
  // A case with no notes starts with Notes collapsed.
  const closeNotes = useCallback(
    () => setOpenSections((open) => open.filter((v) => v !== 'notes')),
    [],
  );
  // Another tab sending staff here (T-212); the pre-need section opens with it.
  const openTabFromRequest = useCallback((tab: string) => {
    setActiveTab(tab);
    if (tab === 'details')
      setOpenSections((open) => (open.includes('preneed') ? open : [...open, 'preneed']));
  }, []);
  useSidebarTabRequests(openTabFromRequest);

  if (!record) return null;

  const delivery = deliveryStatus(record);

  // Ontario files the Registrar General's Form 15; everywhere else files the
  // Québec Déclaration de décès. Same sidebar slot, different document.
  const statementLabel =
    ((record.region as string) || '').toLowerCase() === 'ontario'
      ? 'Form 15 Submitted'
      : 'DEC Submitted';

  // Extra rows for the Deceased section
  const deceasedExtra = (
    <>
      {record.date_time_of_death && (
        <Row label="Death Date">
          <span>
            {new Date(record.date_time_of_death).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
        </Row>
      )}
      {hasPlaceOfDeath(record) && (
        <Row label="Place of Death">
          <PlaceOfDeathValue funeral={record} />
        </Row>
      )}
      {hasPlaceOfDeath(record) && (
        <Row label="Transport Zone">
          <FuneralTransportZone funeral={record} />
        </Row>
      )}
      {record.sp3_number && (
        <Row label="SP3">
          <span>{record.sp3_number}</span>
        </Row>
      )}
    </>
  );

  return (
    <div className="w-96 bg-background flex flex-col h-full min-h-0">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full min-h-0">
        <div className="pt-4">
          <TabsList className="w-full border-b border-border px-5">
            <TabsTrigger value="details" className="flex-none">
              Details
            </TabsTrigger>
            <TabsTrigger value="timeline" className="flex-none">
              Timeline
            </TabsTrigger>
            <TabsTrigger value="contracts" className="flex-none">
              Contracts
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex-none">
              Documents
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Details Tab */}
        <TabsContent value="details" className="flex-1 min-h-0 mt-0">
          <ScrollArea className="h-full">
            <div className="pb-6 pt-1">
              <Accordion type="multiple" value={openSections} onValueChange={setOpenSections}>
                {/* Pre-need cases only: the Pre-plan only switch (T-172) sits
                    above everything so it's seen without opening anything. */}
                <PreplanSidebarSection />

                {/* After fruition: the TruStage claim (T-174). */}
                <TrustageClaimSection />

                {/* Notes next: key info and the latest note before anything else. */}
                <NotesCard onOpen={openNotes} onEmpty={closeNotes} />

                {/* Ontario's two optional services (DROPOFF_LEDGER_PLAN Q5),
                    independent of each other: the private visitation is held
                    at the drop-off facility, the witness of cremation at the
                    crematorium. Cleo sets each confirmed time here after the
                    back-and-forth with the facility; there is no facility
                    confirm step. When one is present the other is shown as an
                    explicit "No", not omitted: "no witness" is a fact the
                    family chose, and a blank row reads the same as a case
                    nobody has looked at. */}
                {(record.has_visitation || record.has_private_identification) && (
                  <AccordionItem value="visitation">
                    <AccordionTrigger icon={<Eye className="w-4 h-4" />}>
                      Visitation &amp; Witness
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2">
                        <Row label="Private visitation">
                          <div className="text-right">
                            {record.has_private_identification ? (
                              <>
                                <SidebarDateTime value={record.private_identification_at} />
                                <div className="text-xs text-muted-foreground">at drop-off</div>
                              </>
                            ) : (
                              <span className="text-muted-foreground">✕ Not purchased</span>
                            )}
                          </div>
                        </Row>
                        <Row label="Witness of cremation">
                          <div className="text-right">
                            {record.has_visitation ? (
                              <>
                                <SidebarDateTime value={record.visitation_datetime} />
                                <div className="text-xs text-muted-foreground">at crematorium</div>
                              </>
                            ) : (
                              <span className="text-muted-foreground">✕ Not purchased</span>
                            )}
                            {/* Retired portal confirm step: older cases may
                                still carry a facility-confirmed time. Shown,
                                never edited. */}
                            {record.witness_confirmed_at && (
                              <div className="text-xs text-muted-foreground">
                                Facility confirmed{' '}
                                <SidebarDateTime value={record.witness_confirmed_at} />
                                {record.witness_confirmed_by
                                  ? ` · ${record.witness_confirmed_by}`
                                  : ''}
                              </div>
                            )}
                          </div>
                        </Row>
                        <EditInDialogButton fullWidth maxWidth="sm" label="Set Date & Time">
                          <SimpleForm toolbar={<ClientSaveToolbar />}>
                            {record.has_private_identification && (
                              <DateTimeInput
                                source="private_identification_at"
                                label="Private visitation (at drop-off)"
                                helperText="Confirmed time at the drop-off facility."
                                fullWidth
                              />
                            )}
                            {record.has_visitation && (
                              <DateTimeInput
                                source="visitation_datetime"
                                label="Witness of cremation (at crematorium)"
                                helperText="Confirmed time at the crematorium."
                                fullWidth
                              />
                            )}
                          </SimpleForm>
                        </EditInDialogButton>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Drop-off facility's ledger entries (read-only). Renders
                    nothing without a drop-off location. */}
                <DropoffSidebarSection />

                {/* Paperwork (was "Status"; the "status" key is unchanged).
                    Government filings, benefits forms and onboarding. */}
                <AccordionItem value="status">
                  <AccordionTrigger icon={<ClipboardList className="w-4 h-4" />}>
                    Paperwork
                  </AccordionTrigger>
                  <AccordionContent>
                    <BenefitsStatusRefreshProvider>
                    <div className="space-y-0.5">
                      {/* Statement-of-death row: the name is a blue link that
                          opens the field-validation ("verify") modal, with the
                          validation icon inline; the right-side badge sets the
                          status. Ontario files Form 15, Québec the DEC — the
                          slot is the same, the document is not. */}
                      <div className="flex justify-between items-center py-1.5">
                        <ValidationStatusIndicator
                          formType="dec"
                          label={statementLabel}
                        />
                        <SidebarDecToggle />
                      </div>
                      {/* Ontario's other two registrar filings. Same group as
                          the statement-of-death row because together they are
                          Ontario's equivalent of the Québec DEC workflow.
                          Renders nothing outside Ontario. */}
                      <OntarioPaperworkRows />
                      <DeathCertificateOrderRows />
                      {/* SP-3 step in SIED (Québec only; renders nothing
                          elsewhere). Our step is the funeral-rep signature. */}
                      <SiedStatusSidebarRow />
                      {/* Divider: DEC group above, Benefits group below. */}
                      <div className="border-t border-border my-1" />
                      {/* Benefits headline: the status badge IS the action —
                          "Missing info" opens the field checklist, "Ready to send"/
                          "Sent" opens the next outstanding form. The ⋮ marks the
                          packet completed/ineligible. Per-form rows are below. */}
                      <div className="flex justify-between items-center py-1.5">
                        <span className="text-[13px] font-medium text-foreground">Benefits</span>
                        <div className="flex items-center gap-1.5">
                          <BenefitsHeadlineBadge />
                          <BenefitsStatusOverrideMenu />
                        </div>
                      </div>
                      {/* Ineligible reason / confirmation # — packet-level, so
                          it sits under the headline, above the per-form rows. */}
                      <BenefitsStatusNote />
                      <FormConfirmationStatusRows />
                      <Row label="Onboarding Part 1">
                        <SidebarStatusToggle source="paperwork1_complete" readyLabel="Done" notReadyLabel="Pending" />
                      </Row>
                      <Row label="Onboarding Part 2">
                        <SidebarStatusToggle source="paperwork2_complete" readyLabel="Done" notReadyLabel="Pending" />
                      </Row>
                    </div>
                    </BenefitsStatusRefreshProvider>
                  </AccordionContent>
                </AccordionItem>

                {/* Delivery: the ashes' delivery and the urn, directly below
                    Paperwork. Two cards, not one: the urn is bought, ordered
                    and shipped on its own schedule, and only the ashes are
                    what the delivery card is about. */}
                <AccordionItem value="delivery">
                  <AccordionTrigger icon={<Truck className="w-4 h-4" />}>
                    <span className="flex items-center gap-2">
                      Delivery
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          delivery.done ? 'bg-muted text-muted-foreground' : 'bg-amber-100 text-amber-900'
                        }`}
                      >
                        {delivery.label}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2">
                      <RemainsLine />
                      <DeliveryCard />
                      <UrnCard />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Deceased */}
                <ClientSection
                  source="deceased_client_id"
                  role="Deceased"
                  extraRows={deceasedExtra}
                  hideAddress
                  showSpouse
                />

                {/* All funeral clients — role-based consolidated, plus associated */}
                <FuneralClientsSidebarSection />

                {/* Partners */}
                <AccordionItem value="partners">
                  <AccordionTrigger icon={<Handshake className="w-4 h-4" />}>
                    Partners
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2.5">
                      <CremationPartnersDisplay />
                      <PartnerVisibilityToggle />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Quote */}
                <QuoteSection funeralId={record.id} />

                {/* Source */}
                {(record as any).lead_source && (
                  <AccordionItem value="source">
                    <AccordionTrigger icon={<Globe className="w-4 h-4" />}>
                      Source
                    </AccordionTrigger>
                    <AccordionContent>
                      <div>
                        {(record as any).lead_source?.channel && (
                          <Row label="Channel">
                            <span>{(record as any).lead_source.channel}</span>
                          </Row>
                        )}
                        {(record as any).lead_source?.landing_page && (
                          <Row label="Landing">
                            <span>{(record as any).lead_source.landing_page}</span>
                          </Row>
                        )}
                        {(record as any).lead_source?.campaign && (
                          <Row label="Campaign">
                            <span>{(record as any).lead_source.campaign}</span>
                          </Row>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Onboarding history (debug) */}
                <OnboardingHistorySection funeralId={record.id} />
              </Accordion>
            </div>
          </ScrollArea>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="flex-1 min-h-0 mt-0">
          <ScrollArea className="h-full">
            <ProcessTimeline />
          </ScrollArea>
        </TabsContent>

        {/* Contracts Tab */}
        <TabsContent value="contracts" className="flex-1 min-h-0 mt-0">
          <ScrollArea className="h-full">
            <PreplanContractsNote />
            <ContractsTab />
          </ScrollArea>
        </TabsContent>

        {/* Documents Tab — always mounted so it pre-fetches data, hidden when inactive */}
        <div className={`flex-1 min-h-0 ${activeTab === 'documents' ? '' : 'hidden'}`}>
          <ScrollArea className="h-full">
            <DocumentsTab />
          </ScrollArea>
        </div>
      </Tabs>
    </div>
  );
}
